// "Your turn", once the board has stopped moving.

import { useEffect, useState } from "react";

import { useGameState } from "src/client/ai/hooks/selectors";
import { useNet } from "src/client/ai/hooks/useNet";
import { TurnBanner } from "../TurnBanner";
import type { BoardInteraction } from "../useBoardInteraction";
import { useSessionView } from "../useSessionView";
import { useBoardHandle } from "./BoardHandle";
import { useShellMode } from "./ShellMode";

/**
 * Mounted unconditionally: the settling watch is what decides whether there is
 * a banner at all, so a gate outside it would be a gate on the answer.
 */
export function TurnBannerZone(_props: {
  /** @deprecated Unread since day 8 — the zone reads the session's view. */
  board_?: BoardInteraction;
}) {
  const board = useBoardHandle();
  const { net } = useNet();
  const { controlsTurn, turnRow } = useSessionView();
  const state = useGameState();
  const { scenario } = useShellMode();

  // Announced only when the board actually comes back to this player: in a
  // hot-seat game that is every turn and the name is what matters, and online
  // or in single player it is only *your* turn that is news. `controlsTurn`
  // is also false while looking back, so a turn that arrives under history
  // is announced when Latest is pressed, not over the board being read.
  // Keyed on the turn counter, not the move list: a turn spent forging adds no
  // move to the history, and the banner would sit out the turn that a player
  // most needs telling about. The counter is the one fact here still read off
  // the state — `Status` has `ply`, which is the move list.
  const yoursNow = controlsTurn && !turnRow.terminal;

  /**
   * The turn the banner is allowed to announce.
   *
   * Held back until the board has stopped moving. The clock passes the moment
   * the opponent's command applies, which is *before* its piece has finished
   * travelling — so "Your turn" arrived over the top of the move it was
   * telling you about, and in single player, where the camera is also still
   * flying to the action, it read as the game getting ahead of itself.
   */
  const [settledTurn, setSettledTurn] = useState(-1);
  const liveTurn = state.industry.turnCounter;
  useEffect(() => {
    if (!yoursNow) return;
    if (board.current?.isSettled()) {
      setSettledTurn(liveTurn);
      return;
    }
    return board.current?.onFrame(() => {
      if (board.current?.isSettled()) setSettledTurn(liveTurn);
    });
  }, [board, yoursNow, liveTurn]);

  if (!yoursNow || settledTurn !== liveTurn) return null;
  return (
    <TurnBanner
      label={scenario || net.seat ? "Your turn" : `${turnRow.sideToMove} to move`}
      turn={settledTurn}
    />
  );
}
